export type SupportedLanguage = 'ar' | 'en' | 'hi' | 'ur' | 'bn' | 'ne' | 'tl';

export interface LanguageInfo {
  code: SupportedLanguage;
  name: string;
  nativeName: string;
  dir: 'rtl' | 'ltr';
  flag: string;
}

export const SUPPORTED_LANGUAGES: LanguageInfo[] = [
  { code: 'ar', name: 'Arabic', nativeName: 'العربية', dir: 'rtl', flag: '🇸🇦' },
  { code: 'en', name: 'English', nativeName: 'English', dir: 'ltr', flag: '🇬🇧' },
  { code: 'hi', name: 'Hindi', nativeName: 'हिन्दी', dir: 'ltr', flag: '🇮🇳' },
  { code: 'ur', name: 'Urdu', nativeName: 'اردو', dir: 'rtl', flag: '🇵🇰' },
  { code: 'bn', name: 'Bengali', nativeName: 'বাংলা', dir: 'ltr', flag: '🇧🇩' },
  { code: 'ne', name: 'Nepali', nativeName: 'नेपाली', dir: 'ltr', flag: '🇳🇵' },
  { code: 'tl', name: 'Tagalog', nativeName: 'Tagalog / Filipino', dir: 'ltr', flag: '🇵🇭' },
];

export const posTranslations: Record<SupportedLanguage, Record<string, string>> = {
  ar: {
    // Top banner
    posTitle: 'نقطة البيع السريعة',
    live: 'مباشر',
    zatcaPhase: 'معتمد ZATCA المرحلة 1 و 2',
    vatNumber: 'الرقم الضريبي',
    quickAdd: '+ إضافة صنف سريع',
    bulkCsv: 'استيراد بالجملة (CSV)',
    storeProductsTab: 'أصناف المتجر',
    fullScreen: 'ملء الشاشة',
    cartMobile: 'السلة',

    // Tabs
    tabFastTiles: '⚡ الأصناف السريعة',
    tabKeypad: '🔢 لوحة الأرقام',
    tabCatalog: '📦 كتالوج البيع',
    tabStoreProducts: '🏷️ أصناف المتجر',

    // Keypad tab
    customItemLabelPlaceholder: 'وصف الصنف المخصص (اختياري)...',
    keypadDisplayTitle: 'المبلغ المدخل (شامل الضريبة 15%)',
    clear: 'مسح',
    addToOrder: 'إضافة للطلب',
    enterValidAmount: 'يرجى إدخال مبلغ صالح',

    // Catalog tab
    searchPlaceholder: 'ابحث بالاسم أو الباركود أو الكود...',
    sku: 'كود',

    // Store products tab
    registeredItems: 'صنف مسجل',
    storeProductsSub: 'أصناف متجرك المتاحة للبيع الفوري',
    addSingleProduct: '+ صنف جديد',
    bulkCsvImport: 'استيراد بالجملة (Bulk CSV)',
    searchProductsPlaceholder: 'ابحث في منتجات المتجر...',
    allCategories: 'جميع التصنيفات',
    colArabicName: 'الاسم بالعربي',
    colEnglishName: 'الاسم بالإنجليزي',
    colPrice: 'السعر (\uE900)',
    colCategory: 'التصنيف',
    colSku: 'الباركود / SKU',
    colActions: 'إجراءات',
    noProductsFound: 'لم يتم العثور على أصناف تطابق البحث',
    quickAddPrompt: 'أضف صنف جديد الآن',

    // Bill / Checkout
    orderBill: 'فاتورة العميل',
    itemsSelected: 'أصناف مختارة',
    emptyCartTitle: 'السلة فارغة حالياً',
    emptyCartDesc: 'اضغط على أي صنف أو استخدم لوحة الأرقام لإضافته للطلب',
    clearCart: 'مسح السلة',
    unitPrice: 'للوحدة',
    subtotal: 'المجموع قبل الضريبة:',
    vatAmount: 'ضريبة القيمة المضافة (15%):',
    grandTotal: 'المبلغ الإجمالي شامل الضريبة:',
    printReceiptBtn: 'طباعة الإيصال الفوري (80mm)',
    emptyCartWarning: 'السلة فارغة، يرجى إضافة أصناف قبل الطباعة',
    itemAddedToast: 'تمت إضافة',

    // Modals
    editProduct: 'تعديل الصنف',
    saveChanges: 'حفظ التعديلات',
    cancel: 'إلغاء',
    deleteConfirm: 'هل أنت متأكد من حذف الصنف؟',
    bulkModalTitle: 'إضافة أصناف بالجملة • Bulk CSV Import',
    bulkModalDesc: 'الصق أسطر CSV أو انسخ من ملف Excel لإضافة عدة أصناف فوراً',
    downloadSampleCsv: 'تحميل نموذج CSV',
    importNowBtn: 'استيراد كافة الأصناف الآن',
    uploadingItems: 'جاري رفع الأصناف...',
    requiredColumnsOrder: 'ترتيب الأعمدة المطلوب: 1. الاسم بالعربي • 2. الاسم بالإنجليزي • 3. السعر بالريال • 4. التصنيف • 5. الباركود',

    // VAT & Expense Hub Tab & Components
    tabVatHub: '📊 مركز الضريبة والمصروفات (VAT Hub)',
    vatHubTitle: 'مركز ضريبة القيمة المضافة والمصروفات (VAT & Expense Hub)',
    vatHubSubtitle: 'موازنة ضريبة مبيعات الكاشير مع فواتير المشتريات وإصدار الإقرار الفصلي المعتمد',
    quarterQ1: 'الربع 1 (Q1)',
    quarterQ2: 'الربع 2 (Q2)',
    quarterQ3: 'الربع 3 (Q3)',
    quarterQ4: 'الربع 4 (Q4)',
    recordExpenseBtn: 'تسجيل فاتورة مورد / مصروف',
    zatcaForm100Btn: 'نموذج الإقرار الضريبي (100)',
    calculatingVatBalance: 'جاري حساب موازنة الضريبة المحدثة...',
    outputVatTitle: 'ضريبة المبيعات المحصلة (ضريبة المخرجات)',
    box14Badge: 'البند 14',
    inputVatTitle: 'ضريبة المشتريات المخصومة (ضريبة المدخلات)',
    box15Badge: 'البند 15',
    netVatDueTitle: 'صافي الضريبة المستحقة للهيئة (واجبة السداد)',
    netVatRefundTitle: 'رصيد ضريبي دائن (مسترد من الهيئة)',
    box16Badge: 'البند 16',
    zatcaPolicyTitle: 'قاعدة الامتثال الضريبي لهيئة الزكاة والضريبة والجمارك (ZATCA):',
    zatcaPolicyDeductible: '✅ يُخصم 15% ضريبة مدخلات من: مشتريات البضاعة والمخزون، بنزين ووقود المركبات، فواتير الكهرباء والمياه، فواتير الاتصالات والإنترنت، إيجار المحل، وأدوات التغليف والصيانة.',
    zatcaPolicyNonDeductible: '❌ لا تُخصم ضريبة من: رواتب ومستحقات الموظفين، الرسوم والغرامات الحكومية ورخص البلدية، ونفقات الضيافة والترفيه الشخصية.',
    expenseLogTitle: 'سجل فواتير المشتريات والمصروفات المسجلة (ضريبة المدخلات)',
    expenseLogSub: 'جميع الفواتير المرفقة تضغط فورياً بوضوح 1800px وبحجم <250KB لتقليل استهلاك المساحة',
    addBillBtn: 'إضافة فاتورة',
    colExpenseDate: 'التاريخ',
    colVendor: 'المورد / الجهة',
    colExpenseCategory: 'التصنيف',
    colPaymentMethod: 'طريقة السداد',
    colNetAmount: 'المبلغ قبل الضريبة',
    colVat15: 'الضريبة 15%',
    colTotalPaid: 'الإجمالي المدفوع',
    colReceipt: 'صورة الفاتورة',
    noExpensesLogged: 'لا توجد فواتير مصروفات مسجلة حتى الآن. اضغط على "تسجيل فاتورة مورد" لإضافة فواتير الوقود والكهرباء والمشتريات.',
    viewReceiptTooltip: 'عرض صورة الفاتورة الأصلية بوضوح كامل',
    noAttachment: 'بدون مرفق',
    previewReceiptModalTitle: 'معاينة الفاتورة الأصلية (وضوح عالي 1800px)',
    sarCurrency: 'ر.س',
  },

  en: {
    // Top banner
    posTitle: 'Quick POS Cashier',
    live: 'LIVE',
    zatcaPhase: 'ZATCA Certified Phase 1 & 2',
    vatNumber: 'VAT Number',
    quickAdd: '+ Quick Add Item',
    bulkCsv: 'Bulk Import (CSV)',
    storeProductsTab: 'Store Products',
    fullScreen: 'Fullscreen',
    cartMobile: 'Cart',

    // Tabs
    tabFastTiles: '⚡ Fast Tiles',
    tabKeypad: '🔢 Numpad / Custom',
    tabCatalog: '📦 Catalog',
    tabStoreProducts: '🏷️ Store Products',

    // Keypad tab
    customItemLabelPlaceholder: 'Custom item note (optional)...',
    keypadDisplayTitle: 'ENTERED AMOUNT (INCL. 15% VAT)',
    clear: 'Clear',
    addToOrder: 'Add to Order',
    enterValidAmount: 'Please enter a valid price amount',

    // Catalog tab
    searchPlaceholder: 'Search by name, barcode, or SKU...',
    sku: 'SKU',

    // Store products tab
    registeredItems: 'items registered',
    storeProductsSub: 'Store catalog products ready for instant sale',
    addSingleProduct: '+ Add Product',
    bulkCsvImport: 'Bulk Import (CSV)',
    searchProductsPlaceholder: 'Search store products...',
    allCategories: 'All Categories',
    colArabicName: 'Arabic Name',
    colEnglishName: 'English Name',
    colPrice: 'Price (\uE900)',
    colCategory: 'Category',
    colSku: 'SKU / Barcode',
    colActions: 'Actions',
    noProductsFound: 'No products match your search',
    quickAddPrompt: 'Add your first product now',

    // Bill / Checkout
    orderBill: 'Customer Bill',
    itemsSelected: 'items selected',
    emptyCartTitle: 'Cart is empty',
    emptyCartDesc: 'Tap any product or enter price to add to order',
    clearCart: 'Clear Cart',
    unitPrice: 'each',
    subtotal: 'Subtotal (Excl. VAT):',
    vatAmount: 'VAT Amount (15%):',
    grandTotal: 'Grand Total (Incl. VAT):',
    printReceiptBtn: 'Print Instant Receipt (80mm)',
    emptyCartWarning: 'Cart is empty. Please add items before printing receipt.',
    itemAddedToast: 'Added',

    // Modals
    editProduct: 'Edit Product',
    saveChanges: 'Save Changes',
    cancel: 'Cancel',
    deleteConfirm: 'Are you sure you want to delete this product?',
    bulkModalTitle: 'Bulk Products Import (CSV / Excel)',
    bulkModalDesc: 'Paste CSV rows or copy from Excel to upload dozens of products at once',
    downloadSampleCsv: 'Download Sample CSV',
    importNowBtn: 'Import All Products Now',
    uploadingItems: 'Uploading products...',
    requiredColumnsOrder: 'Column Order: 1. Arabic Name • 2. English Name • 3. Price (\uE900) • 4. Category • 5. SKU',

    // VAT & Expense Hub Tab & Components
    tabVatHub: '📊 VAT & Expense Hub',
    vatHubTitle: 'VAT & Expense Hub (مركز الضريبة والمصروفات)',
    vatHubSubtitle: 'Balance POS sales VAT with purchase invoices and generate official quarterly ZATCA Form 100 returns',
    quarterQ1: 'Quarter 1 (Q1)',
    quarterQ2: 'Quarter 2 (Q2)',
    quarterQ3: 'Quarter 3 (Q3)',
    quarterQ4: 'Quarter 4 (Q4)',
    recordExpenseBtn: 'Record Vendor Bill / Expense',
    zatcaForm100Btn: 'ZATCA Form 100 Return',
    calculatingVatBalance: 'Calculating live VAT balance...',
    outputVatTitle: 'Collected Sales VAT (Output VAT)',
    box14Badge: 'Box 14',
    inputVatTitle: 'Deductible Purchases VAT (Input VAT)',
    box15Badge: 'Box 15',
    netVatDueTitle: 'Net VAT Due to ZATCA (Payable)',
    netVatRefundTitle: 'Net VAT Credit (Refundable from ZATCA)',
    box16Badge: 'Box 16',
    zatcaPolicyTitle: 'ZATCA VAT Deduction Compliance Policy:',
    zatcaPolicyDeductible: '✅ 15% Input VAT is deductible from: Inventory & stock purchases, vehicle fuel/gasoline, electricity & water bills, telecom & internet, store/office rent, packaging and maintenance.',
    zatcaPolicyNonDeductible: '❌ NOT deductible: Employee salaries & benefits, government fees/penalties/municipality licenses, and personal entertainment/hospitality.',
    expenseLogTitle: 'Purchase Invoices & Expense Log (Input VAT)',
    expenseLogSub: 'All attached receipts are compressed instantly to 1800px (<250KB) to minimize storage',
    addBillBtn: 'Add Bill',
    colExpenseDate: 'Date',
    colVendor: 'Vendor / Beneficiary',
    colExpenseCategory: 'Category',
    colPaymentMethod: 'Payment Method',
    colNetAmount: 'Net (Excl. VAT)',
    colVat15: 'VAT (15%)',
    colTotalPaid: 'Total Paid',
    colReceipt: 'Receipt',
    noExpensesLogged: 'No expense bills logged yet. Click "Record Vendor Bill" to log fuel, utility, and purchase receipts.',
    viewReceiptTooltip: 'View full-resolution original receipt',
    noAttachment: 'No attachment',
    previewReceiptModalTitle: 'Original Receipt Full Resolution Preview (1800px)',
    sarCurrency: 'SAR',
  },

  hi: {
    // Top banner
    posTitle: 'त्वरित पीओएस काउंटर',
    live: 'लाइव',
    zatcaPhase: 'ZATCA चरण 1 और 2 प्रमाणित',
    vatNumber: 'वैट नंबर',
    quickAdd: '+ तुरंत आइटम जोड़ें',
    bulkCsv: 'थोक आयात (CSV)',
    storeProductsTab: 'दुकान उत्पाद',
    fullScreen: 'पूर्ण स्क्रीन',
    cartMobile: 'कार्ट',

    // Tabs
    tabFastTiles: '⚡ त्वरित आइटम (Tiles)',
    tabKeypad: '🔢 कीपैड (Keypad)',
    tabCatalog: '📦 उत्पाद सूची',
    tabStoreProducts: '🏷️ दुकान उत्पाद',

    // Keypad tab
    customItemLabelPlaceholder: 'आइटम का नाम / विवरण (वैकल्पिक)...',
    keypadDisplayTitle: 'दर्ज राशि (15% वैट सहित)',
    clear: 'मिटाएं',
    addToOrder: 'ऑर्डर में जोड़ें',
    enterValidAmount: 'कृपया मान्य राशि दर्ज करें',

    // Catalog tab
    searchPlaceholder: 'नाम, बारकोड या कोड द्वारा खोजें...',
    sku: 'कोड',

    // Store products tab
    registeredItems: 'उत्पाद पंजीकृत',
    storeProductsSub: 'बिक्री के लिए तैयार आपकी दुकान के उत्पाद',
    addSingleProduct: '+ नया उत्पाद',
    bulkCsvImport: 'थोक आयात (CSV)',
    searchProductsPlaceholder: 'दुकान के उत्पाद खोजें...',
    allCategories: 'सभी श्रेणियां',
    colArabicName: 'अरबी नाम',
    colEnglishName: 'अंग्रेज़ी नाम',
    colPrice: 'कीमत (\uE900)',
    colCategory: 'श्रेणी',
    colSku: 'बारकोड / SKU',
    colActions: 'कार्रवाई',
    noProductsFound: 'कोई उत्पाद नहीं मिला',
    quickAddPrompt: 'पहला उत्पाद जोड़ें',

    // Bill / Checkout
    orderBill: 'ग्राहक बिल',
    itemsSelected: 'आइटम चुने गए',
    emptyCartTitle: 'कार्ट अभी खाली है',
    emptyCartDesc: 'आइटम जोड़ने के लिए किसी भी उत्पाद पर टैप करें',
    clearCart: 'कार्ट खाली करें',
    unitPrice: 'प्रति इकाई',
    subtotal: 'उप-योग (वैट बिना):',
    vatAmount: 'वैट राशि (15%):',
    grandTotal: 'कुल देय राशि (वैट सहित):',
    printReceiptBtn: 'तुरंत रसीद प्रिंट करें (80mm)',
    emptyCartWarning: 'कार्ट खाली है, कृपया पहले आइटम जोड़ें',
    itemAddedToast: 'जोड़ा गया',

    // Modals
    editProduct: 'उत्पाद संपादित करें',
    saveChanges: 'परिवर्तन सहेजें',
    cancel: 'रद्द करें',
    deleteConfirm: 'क्या आप वाकई इस उत्पाद को हटाना चाहते हैं?',
    bulkModalTitle: 'थोक उत्पाद आयात (CSV / Excel)',
    bulkModalDesc: 'एक साथ कई आइटम जोड़ने के लिए CSV पंक्तियाँ पेस्ट करें',
    downloadSampleCsv: 'नमूना CSV डाउनलोड करें',
    importNowBtn: 'सभी उत्पाद अभी आयात करें',
    uploadingItems: 'उत्पाद अपलोड हो रहे हैं...',
    requiredColumnsOrder: 'कॉलम क्रम: 1. अरबी नाम • 2. अंग्रेज़ी नाम • 3. मूल्य (\uE900) • 4. श्रेणी • 5. बारकोड',

    // VAT & Expense Hub Tab & Components
    tabVatHub: '📊 वैट एवं व्यय हब (VAT Hub)',
    vatHubTitle: 'वैट एवं व्यय हब (VAT & Expense Hub)',
    vatHubSubtitle: 'बिक्री वैट को खरीद बिलों के साथ संतुलित करें और ZATCA रिटर्न तैयार करें',
    quarterQ1: 'तिमाही 1 (Q1)',
    quarterQ2: 'तिमाही 2 (Q2)',
    quarterQ3: 'तिमाही 3 (Q3)',
    quarterQ4: 'तिमाही 4 (Q4)',
    recordExpenseBtn: 'विक्रेता बिल / खर्च दर्ज करें',
    zatcaForm100Btn: 'ZATCA फॉर्म 100 रिटर्न',
    calculatingVatBalance: 'वैट गणना हो रही है...',
    outputVatTitle: 'बिक्री पर एकत्र वैट (आउटपुट वैट)',
    box14Badge: 'बॉक्स 14',
    inputVatTitle: 'खरीद पर कटौती योग्य वैट (इनपुट वैट)',
    box15Badge: 'बॉक्स 15',
    netVatDueTitle: 'ZATCA को देय शुद्ध वैट (भुगतान योग्य)',
    netVatRefundTitle: 'शुद्ध वैट क्रेडिट (वापसी योग्य)',
    box16Badge: 'बॉक्स 16',
    zatcaPolicyTitle: 'ZATCA वैट कटौती अनुपालन नीति:',
    zatcaPolicyDeductible: '✅ 15% इनपुट वैट कटौती योग्य: इन्वेंट्री, वाहन ईंधन, बिजली/पानी, इंटरनेट/टेलीकॉम, स्टोर किराया व रखरखाव।',
    zatcaPolicyNonDeductible: '❌ कटौती योग्य नहीं: कर्मचारियों का वेतन, सरकारी शुल्क/जुर्माना/लाइसेंस, और व्यक्तिगत आतिथ्य।',
    expenseLogTitle: 'खरीद बिल एवं व्यय रजिस्टर (इनपुट वैट)',
    expenseLogSub: 'सभी रसीदें 1800px (<250KB) में संपीड़ित की जाती हैं',
    addBillBtn: 'बिल जोड़ें',
    colExpenseDate: 'तारीख',
    colVendor: 'विक्रेता / संस्थान',
    colExpenseCategory: 'श्रेणी',
    colPaymentMethod: 'भुगतान विधि',
    colNetAmount: 'वैट बिना राशि',
    colVat15: 'वैट (15%)',
    colTotalPaid: 'कुल भुगतान',
    colReceipt: 'रसीद',
    noExpensesLogged: 'कोई खर्च दर्ज नहीं है। ईंधन, बिजली और खरीद बिल दर्ज करने के लिए "विक्रेता बिल दर्ज करें" पर क्लिक करें।',
    viewReceiptTooltip: 'मूल रसीद देखें',
    noAttachment: 'कोई रसीद नहीं',
    previewReceiptModalTitle: 'मूल रसीद पूर्वावलोकन (1800px)',
    sarCurrency: 'SAR',
  },

  ur: {
    // Top banner
    posTitle: 'فوری پی او ایس کیشیر',
    live: 'لائیو',
    zatcaPhase: 'زکوٰۃ و ٹیکس ZATCA منظور شدہ',
    vatNumber: 'ٹیکس نمبر',
    quickAdd: '+ فوری آئٹم شامل کریں',
    bulkCsv: 'بلک درآمد (CSV)',
    storeProductsTab: 'دکان کی اشیاء',
    fullScreen: 'مکمل اسکرین',
    cartMobile: 'ٹوکری',

    // Tabs
    tabFastTiles: '⚡ تیز رفتار آئٹمز',
    tabKeypad: '🔢 کی پیڈ (Keypad)',
    tabCatalog: '📦 کیٹلاگ',
    tabStoreProducts: '🏷️ اسٹور کی مصنوعات',

    // Keypad tab
    customItemLabelPlaceholder: 'آئٹم کی تفصیل (اختیاری)...',
    keypadDisplayTitle: 'درج کردہ رقم (15% ٹیکس سمیت)',
    clear: 'صاف کریں',
    addToOrder: 'آرڈر میں شامل کریں',
    enterValidAmount: 'براہ کرم درست رقم درج کریں',

    // Catalog tab
    searchPlaceholder: 'نام، بارکوڈ یا SKU سے تلاش کریں...',
    sku: 'کوڈ',

    // Store products tab
    registeredItems: 'آئٹمز درج ہیں',
    storeProductsSub: 'فوری فروخت کے لیے تیار آپ کی دکان کا کیٹلاگ',
    addSingleProduct: '+ نیا آئٹم',
    bulkCsvImport: 'بلک درآمد (CSV)',
    searchProductsPlaceholder: 'اشیاء تلاش کریں...',
    allCategories: 'تمام اقسام',
    colArabicName: 'عربی نام',
    colEnglishName: 'انگریزی نام',
    colPrice: 'قیمت (\uE900)',
    colCategory: 'قسم / Category',
    colSku: 'بارکوڈ / SKU',
    colActions: 'اقدامات',
    noProductsFound: 'کوئی آئٹم نہیں ملا',
    quickAddPrompt: 'پہلا آئٹم شامل کریں',

    // Bill / Checkout
    orderBill: 'کسٹمر بل',
    itemsSelected: 'منتخب آئٹمز',
    emptyCartTitle: 'ٹوکری خالی ہے',
    emptyCartDesc: 'آرڈر میں شامل کرنے کے لیے کسی بھی آئٹم پر کلک کریں',
    clearCart: 'ٹوکری خالی کریں',
    unitPrice: 'فی یونٹ',
    subtotal: 'رقم قبل از ٹیکس:',
    vatAmount: 'ٹیکس (15%):',
    grandTotal: 'کل واجب الادا رقم (بشمول ٹیکس):',
    printReceiptBtn: 'فوری رسید پرنٹ کریں (80mm)',
    emptyCartWarning: 'ٹوکری خالی ہے، پرنٹ کرنے سے پہلے اشیاء شامل کریں',
    itemAddedToast: 'شامل کر دیا گیا',

    // Modals
    editProduct: 'آئٹم میں ترمیم کریں',
    saveChanges: 'تبدیلیاں محفوظ کریں',
    cancel: 'منسوخ کریں',
    deleteConfirm: 'کیا آپ واقعی اس آئٹم کو حذف کرنا چاہتے ہیں؟',
    bulkModalTitle: 'بلک آئٹم درآمد (CSV / Excel)',
    bulkModalDesc: 'ایک ساتھ درجنوں اشیاء شامل کرنے کے لیے CSV پیسٹ کریں',
    downloadSampleCsv: 'نمونہ CSV ڈاؤن لوڈ کریں',
    importNowBtn: 'تمام اشیاء ابھی درآمد کریں',
    uploadingItems: 'اشیاء اپ لوڈ ہو رہی ہیں...',
    requiredColumnsOrder: 'کالمز کی ترتیب: 1. عربی نام • 2. انگریزی نام • 3. قیمت (\uE900) • 4. قسم • 5. بارکوڈ',

    // VAT & Expense Hub Tab & Components
    tabVatHub: '📊 ویٹ اور اخراجات ہب (VAT Hub)',
    vatHubTitle: 'ویلیو ایڈڈ ٹیکس اور اخراجات ہب (VAT & Expense Hub)',
    vatHubSubtitle: 'سیلز ٹیکس کو خریداری کے بلوں سے بیلنس کریں اور باضابطہ سہ ماہی ZATCA ریٹرن جاری کریں',
    quarterQ1: 'سہ ماہی 1 (Q1)',
    quarterQ2: 'سہ ماہی 2 (Q2)',
    quarterQ3: 'سہ ماہی 3 (Q3)',
    quarterQ4: 'سہ ماہی 4 (Q4)',
    recordExpenseBtn: 'سپلائر بل / خرچہ درج کریں',
    zatcaForm100Btn: 'ZATCA فارم 100 ریٹرن',
    calculatingVatBalance: 'ٹیکس بیلنس کا حساب ہو رہا ہے...',
    outputVatTitle: 'سیلز پر وصول شدہ ٹیکس (آؤٹ پٹ ویٹ)',
    box14Badge: 'شِق 14',
    inputVatTitle: 'خریداری پر کٹوتی کے قابل ٹیکس (ان پٹ ویٹ)',
    box15Badge: 'شِق 15',
    netVatDueTitle: 'اتھارٹی کو واجب الادا ٹیکس (قابل ادائیگی)',
    netVatRefundTitle: 'کریڈٹ ٹیکس بیلنس (قابل واپسی)',
    box16Badge: 'شِق 16',
    zatcaPolicyTitle: 'زکوٰۃ و ٹیکس اتھارٹی (ZATCA) کی پالیسی:',
    zatcaPolicyDeductible: '✅ 15% ٹیکس کٹوتی: اسٹاک خریداری، گاڑی کا ایندھن، بجلی و پانی کے بل، انٹرنیٹ و فون، دکان کا کرایہ اور مرمت۔',
    zatcaPolicyNonDeductible: '❌ کٹوتی نہیں ہوگی: ملازمین کی تنخواہیں، سرکاری فیس و جرمانے، اور ذاتی مہمان نوازی کے اخراجات۔',
    expenseLogTitle: 'خریداری اور اخراجات کا ریکارڈ (ان پٹ ویٹ)',
    expenseLogSub: 'تمام منسلک رسیدیں فوری 1800px اور 250KB سے کم سائز پر کمپریس ہوتی ہیں',
    addBillBtn: 'بل درج کریں',
    colExpenseDate: 'تاریخ',
    colVendor: 'سپلائر / ادارہ',
    colExpenseCategory: 'شعبہ',
    colPaymentMethod: 'ادائیگی کا طریقہ',
    colNetAmount: 'ٹیکس سے قبل رقم',
    colVat15: 'ٹیکس 15%',
    colTotalPaid: 'کل ادا شدہ',
    colReceipt: 'رسید کی تصویر',
    noExpensesLogged: 'کوئی اخراجات درج نہیں۔ پیٹرول، بجلی اور خریداری کے بل درج کرنے کے لیے "سپلائر بل درج کریں" پر کلک کریں۔',
    viewReceiptTooltip: 'اصل رسید کی تصویر دیکھیں',
    noAttachment: 'کوئی منسلک نہیں',
    previewReceiptModalTitle: 'اصل رسید کا جائزہ (1800px)',
    sarCurrency: 'ر.س',
  },

  bn: {
    // Top banner
    posTitle: 'কুইক পিওএস ক্যাশিয়ার',
    live: 'লাইভ',
    zatcaPhase: 'ZATCA ফেজ ১ এবং ২ সার্টিফাইড',
    vatNumber: 'ভ্যাট নম্বর',
    quickAdd: '+ দ্রুত আইটেম যোগ করুন',
    bulkCsv: 'বাল্ক ইম্পোর্ট (CSV)',
    storeProductsTab: 'দোকানের পণ্য',
    fullScreen: 'ফুলস্ক্রিন',
    cartMobile: 'কার্ট',

    // Tabs
    tabFastTiles: '⚡ দ্রুত আইটেম (Tiles)',
    tabKeypad: '🔢 কীপ্যাড (Keypad)',
    tabCatalog: '📦 ক্যাটালগ',
    tabStoreProducts: '🏷️ স্টোর পণ্য',

    // Keypad tab
    customItemLabelPlaceholder: 'আইটেমের বিবরণ (ঐচ্ছিক)...',
    keypadDisplayTitle: 'প্রবিষ্ট পরিমাণ (১৫% ভ্যাট সহ)',
    clear: 'মুছুন',
    addToOrder: 'অর্ডারে যোগ করুন',
    enterValidAmount: 'দয়া করে একটি সঠিক পরিমাণ লিখুন',

    // Catalog tab
    searchPlaceholder: 'নাম, বারকোড বা কোড দিয়ে খুঁজুন...',
    sku: 'এসকেইউ',

    // Store products tab
    registeredItems: 'আইটেম নিবন্ধিত',
    storeProductsSub: 'বিক্রির জন্য প্রস্তুত আপনার দোকানের পণ্য তালিকা',
    addSingleProduct: '+ নতুন পণ্য',
    bulkCsvImport: 'বাল্ক ইম্পোর্ট (CSV)',
    searchProductsPlaceholder: 'পণ্য খুঁজুন...',
    allCategories: 'সকল ক্যাটাগরি',
    colArabicName: 'আরবি নাম',
    colEnglishName: 'ইংরেজি নাম',
    colPrice: 'মূল্য (\uE900)',
    colCategory: 'ক্যাটাগরি',
    colSku: 'বারকোড / SKU',
    colActions: 'অ্যাকশন',
    noProductsFound: 'কোনো পণ্য পাওয়া যায়নি',
    quickAddPrompt: 'প্রথম পণ্যটি এখনই যোগ করুন',

    // Bill / Checkout
    orderBill: 'গ্রাহকের বিল',
    itemsSelected: 'আইটেম নির্বাচিত',
    emptyCartTitle: 'কার্ট এখন খালি',
    emptyCartDesc: 'অর্ডারে যোগ করতে যেকোনো পণ্যে স্পর্শ করুন',
    clearCart: 'কার্ট পরিষ্কার করুন',
    unitPrice: 'প্রতি ইউনিট',
    subtotal: 'উপমোট (ভ্যাট ছাড়া):',
    vatAmount: 'ভ্যাট (১৫%):',
    grandTotal: 'সর্বমোট প্রদেয় বিল (ভ্যাট সহ):',
    printReceiptBtn: 'তাৎক্ষণিক রশিদ প্রিন্ট করুন (80mm)',
    emptyCartWarning: 'কার্ট খালি, প্রিন্ট করার আগে আইটেম যোগ করুন',
    itemAddedToast: 'যোগ করা হয়েছে',

    // Modals
    editProduct: 'পণ্য সম্পাদনা করুন',
    saveChanges: 'সংরক্ষণ করুন',
    cancel: 'বাতিল',
    deleteConfirm: 'আপনি কি নিশ্চিত যে এই পণ্যটি মুছে ফেলতে চান?',
    bulkModalTitle: 'বাল্ক পণ্য ইম্পোর্ট (CSV / Excel)',
    bulkModalDesc: 'একসাথে অনেক পণ্য যোগ করতে CSV পেস্ট করুন',
    downloadSampleCsv: 'নমুনা CSV ডাউনলোড করুন',
    importNowBtn: 'সকল পণ্য এখনই ইম্পোর্ট করুন',
    uploadingItems: 'পণ্য আপলোড হচ্ছে...',
    requiredColumnsOrder: 'কলাম ক্রম: ১. আরবি নাম • ২. ইংরেজি নাম • ৩. মূল্য (\uE900) • ৪. ক্যাটাগরি • ৫. বারকোড',

    // VAT & Expense Hub Tab & Components
    tabVatHub: '📊 ভ্যাট ও ব্যয় হাব (VAT Hub)',
    vatHubTitle: 'ভ্যাট ও ব্যয় হাব (VAT & Expense Hub)',
    vatHubSubtitle: 'বিক্রয় ভ্যাটকে ক্রয়ের বিলের সাথে ব্যালেন্স করুন এবং ZATCA রিটার্ন তৈরি করুন',
    quarterQ1: 'ত্রৈমাসিক ১ (Q1)',
    quarterQ2: 'ত্রৈমাসিক ২ (Q2)',
    quarterQ3: 'ত্রৈমাসিক ৩ (Q3)',
    quarterQ4: 'ত্রৈমাসিক ৪ (Q4)',
    recordExpenseBtn: 'ভেন্ডর বিল / খরচ রেকর্ড করুন',
    zatcaForm100Btn: 'ZATCA ফর্ম ১০০ রিটার্ন',
    calculatingVatBalance: 'ভ্যাট ব্যালেন্স গণনা করা হচ্ছে...',
    outputVatTitle: 'সংগৃহীত বিক্রয় ভ্যাট (আউটপুট ভ্যাট)',
    box14Badge: 'বক্স ১৪',
    inputVatTitle: 'কর্তনযোগ্য ক্রয় ভ্যাট (ইনপুট ভ্যাট)',
    box15Badge: 'বক্স ১৫',
    netVatDueTitle: 'ZATCA-কে প্রদেয় নেট ভ্যাট',
    netVatRefundTitle: 'নেট ভ্যাট ক্রেডিট (ফেরতযোগ্য)',
    box16Badge: 'বক্স ১৬',
    zatcaPolicyTitle: 'ZATCA ভ্যাট ছাড়ের নীতিমালা:',
    zatcaPolicyDeductible: '✅ ১৫% ইনপুট ভ্যাট ছাড়যোগ্য: পণ্য ক্রয়, গাড়ির জ্বালানি, বিদ্যুৎ ও পানি বিল, ইন্টারনেট, ভাড়া ও রক্ষণাবেক্ষণ।',
    zatcaPolicyNonDeductible: '❌ ছাড়যোগ্য নয়: কর্মচারীদের বেতন, সরকারি ফি ও জরিমানা, এবং ব্যক্তিগত আপ্যায়ন খরচ।',
    expenseLogTitle: 'ক্রয় চালান ও ব্যয় লগ (ইনপুট ভ্যাট)',
    expenseLogSub: 'সংযুক্ত রসিদগুলো তাৎক্ষণিক 1800px (<250KB) আকারে কম্প্রেস করা হয়',
    addBillBtn: 'বিল যোগ করুন',
    colExpenseDate: 'তারিখ',
    colVendor: 'ভেন্ডর / প্রতিষ্ঠান',
    colExpenseCategory: 'ক্যাটাগরি',
    colPaymentMethod: 'পেমেন্ট মাধ্যম',
    colNetAmount: 'ভ্যাট ছাড়া পরিমাণ',
    colVat15: 'ভ্যাট ১৫%',
    colTotalPaid: 'মোট পরিশোধ',
    colReceipt: 'রসিদ',
    noExpensesLogged: 'এখনো কোনো ব্যয়ের বিল রেকর্ড করা হয়নি। বিল যোগ করতে "ভেন্ডর বিল রেকর্ড করুন" ক্লিক করুন।',
    viewReceiptTooltip: 'আসল রসিদ দেখুন',
    noAttachment: 'কোনো সংযুক্তি নেই',
    previewReceiptModalTitle: 'আসল রসিদ প্রিভিউ (1800px)',
    sarCurrency: 'SAR',
  },

  ne: {
    // Top banner
    posTitle: 'द्रुत पीओएस क्यासियर',
    live: 'प्रत्यक्ष (LIVE)',
    zatcaPhase: 'ZATCA चरण १ र २ प्रमाणित',
    vatNumber: 'भ्याट नम्बर',
    quickAdd: '+ तुरुन्त सामान थप्नुहोस्',
    bulkCsv: 'थोक आयात (CSV)',
    storeProductsTab: 'पसलका सामानहरू',
    fullScreen: 'पूरा स्क्रिन',
    cartMobile: 'झोला (Cart)',

    // Tabs
    tabFastTiles: '⚡ द्रुत सामानहरू (Tiles)',
    tabKeypad: '🔢 किप्याड (Keypad)',
    tabCatalog: '📦 सूची (Catalog)',
    tabStoreProducts: '🏷️ पसलका उत्पादनहरू',

    // Keypad tab
    customItemLabelPlaceholder: 'सामानको नाम / विवरण (वैकल्पिक)...',
    keypadDisplayTitle: 'प्रविष्ट रकम (१५% भ्याट सहित)',
    clear: 'हटाउनुहोस्',
    addToOrder: 'अर्डरमा थप्नुहोस्',
    enterValidAmount: 'कृपया मान्य मूल्य प्रविष्ट गर्नुहोस्',

    // Catalog tab
    searchPlaceholder: 'नाम वा बारकोड खोज्नुहोस्...',
    sku: 'कोड',

    // Store products tab
    registeredItems: 'सामान दर्ता गरियो',
    storeProductsSub: 'तपाईंको पसलका तत्काल बिक्रीयोग्य उत्पादनहरू',
    addSingleProduct: '+ नयाँ सामान',
    bulkCsvImport: 'थोक आयात (CSV)',
    searchProductsPlaceholder: 'उत्पादनहरू खोज्नुहोस्...',
    allCategories: 'सबै वर्गहरू',
    colArabicName: 'अरबी नाम',
    colEnglishName: 'अंग्रेजी नाम',
    colPrice: 'मूल्य (\uE900)',
    colCategory: 'वर्ग (Category)',
    colSku: 'बारकोड / SKU',
    colActions: 'कार्यहरू',
    noProductsFound: 'कुनै सामान फेला परेन',
    quickAddPrompt: 'पहिलो सामान थप्नुहोस्',

    // Bill / Checkout
    orderBill: 'ग्राहकको बिल',
    itemsSelected: 'सामान चयन गरियो',
    emptyCartTitle: 'झोला अहिले खाली छ',
    emptyCartDesc: 'अर्डरमा थप्न कुनै पनि सामानमा क्लिक गर्नुहोस्',
    clearCart: 'झोला खाली गर्नुहोस्',
    unitPrice: 'प्रति एकाइ',
    subtotal: 'कुल रकम (भ्याट बाहेक):',
    vatAmount: 'भ्याट रकम (१५%):',
    grandTotal: 'जम्मा तिर्नुपर्ने रकम (भ्याट सहित):',
    printReceiptBtn: 'तुरुन्त रसिद छाप्नुहोस् (80mm)',
    emptyCartWarning: 'झोला खाली छ, कृपया रसिद छाप्नुअघि सामान थप्नुहोस्',
    itemAddedToast: 'थपियो',

    // Modals
    editProduct: 'सामान सम्पादन गर्नुहोस्',
    saveChanges: 'परिवर्तन सुरक्षित गर्नुहोस्',
    cancel: 'रद्द गर्नुहोस्',
    deleteConfirm: 'के तपाईं यो सामान पक्का हटाउन चाहनुहुन्छ?',
    bulkModalTitle: 'थोक उत्पादन आयात (CSV / Excel)',
    bulkModalDesc: 'एकैचोटि धेरै सामान थप्न CSV टाँस्नुहोस्',
    downloadSampleCsv: 'नमूना CSV डाउनलोड गर्नुहोस्',
    importNowBtn: 'सबै सामान अहिले आयात गर्नुहोस्',
    uploadingItems: 'सामान अपलोड हुँदैछ...',
    requiredColumnsOrder: 'कॉलम क्रम: १. अरबी नाम • २. अंग्रेजी नाम • ३. मूल्य (\uE900) • ४. वर्ग • ५. बारकोड',

    // VAT & Expense Hub Tab & Components
    tabVatHub: '📊 भ्याट तथा खर्च हब (VAT Hub)',
    vatHubTitle: 'भ्याट तथा खर्च हब (VAT & Expense Hub)',
    vatHubSubtitle: 'बिक्री भ्याट र खरिद बिलको हिसाब मिलाउनुहोस् र ZATCA विवरण तयार गर्नुहोस्',
    quarterQ1: 'त्रैमासिक १ (Q1)',
    quarterQ2: 'त्रैमासिक २ (Q2)',
    quarterQ3: 'त्रैमासिक ३ (Q3)',
    quarterQ4: 'त्रैमासिक ४ (Q4)',
    recordExpenseBtn: 'विक्रेता बिल / खर्च दर्ता गर्नुहोस्',
    zatcaForm100Btn: 'ZATCA फारम १०० विवरण',
    calculatingVatBalance: 'भ्याट गणना हुँदैछ...',
    outputVatTitle: 'बिक्रीमा संकलित भ्याट (आउटपुट भ्याट)',
    box14Badge: 'बाकस १४',
    inputVatTitle: 'खरिदमा छुट योग्य भ्याट (इनपुट भ्याट)',
    box15Badge: 'बाकस १५',
    netVatDueTitle: 'ZATCA लाई तिर्नुपर्ने कुल भ्याट',
    netVatRefundTitle: 'फिर्ता पाउने भ्याट रकम (क्रेडिट)',
    box16Badge: 'बाकस १६',
    zatcaPolicyTitle: 'ZATCA भ्याट कट्टी नीति:',
    zatcaPolicyDeductible: '✅ १५% भ्याट छुट हुने: सामान खरिद, सवारी इन्धन, बिजुली/पानी, इन्टरनेट, पसल भाडा र मर्मत।',
    zatcaPolicyNonDeductible: '❌ छुट नहुने: कर्मचारी तलब, सरकारी दस्तुर/जरिवाना, र व्यक्तिगत मनोरञ्जन खर्च।',
    expenseLogTitle: 'खरिद बिल तथा खर्च दर्ता (इनपुट भ्याट)',
    expenseLogSub: 'सबै रसिदहरू तत्काल 1800px (<250KB) मा कम्प्रेस हुन्छन्',
    addBillBtn: 'बिल थप्नुहोस्',
    colExpenseDate: 'मिति',
    colVendor: 'विक्रेता / संस्था',
    colExpenseCategory: 'वर्ग',
    colPaymentMethod: 'भुक्तानी विधि',
    colNetAmount: 'भ्याट बिना रकम',
    colVat15: 'भ्याट १५%',
    colTotalPaid: 'जम्मा तिरेको रकम',
    colReceipt: 'रसिद',
    noExpensesLogged: 'कुनै खर्च दर्ता गरिएको छैन। इन्धन वा बिजुली बिल थप्न "विक्रेता बिल दर्ता गर्नुहोस्" मा क्लिक गर्नुहोस्।',
    viewReceiptTooltip: 'मूल रसिद हेर्नुहोस्',
    noAttachment: 'संलग्न छैन',
    previewReceiptModalTitle: 'मूल रसिद पूर्वावलोकन (1800px)',
    sarCurrency: 'SAR',
  },

  tl: {
    // Top banner
    posTitle: 'Mabilisang POS Cashier',
    live: 'LIVE',
    zatcaPhase: 'Sertipikadong ZATCA Phase 1 & 2',
    vatNumber: 'Numero ng VAT',
    quickAdd: '+ Mabilisang Dagdag ng Aytem',
    bulkCsv: 'Maramihang Pag-import (CSV)',
    storeProductsTab: 'Mga Produkto sa Tindahan',
    fullScreen: 'Fullscreen',
    cartMobile: 'Kariton (Cart)',

    // Tabs
    tabFastTiles: '⚡ Mabilisang Pindot (Tiles)',
    tabKeypad: '🔢 Numpad / Custom',
    tabCatalog: '📦 Katalogo',
    tabStoreProducts: '🏷️ Mga Produkto sa Tindahan',

    // Keypad tab
    customItemLabelPlaceholder: 'Pangalan o deskripsyon ng aytem (opsyonal)...',
    keypadDisplayTitle: 'HALAGANG IPINASOK (KASAMA ANG 15% VAT)',
    clear: 'Burahin',
    addToOrder: 'Idagdag sa Order',
    enterValidAmount: 'Mangyaring maglagay ng wastong halaga',

    // Catalog tab
    searchPlaceholder: 'Maghanap gamit ang pangalan o barcode...',
    sku: 'SKU',

    // Store products tab
    registeredItems: 'mga nakarehistrong produkto',
    storeProductsSub: 'Mga produkto ng tindahan na handa para sa benta',
    addSingleProduct: '+ Magdagdag ng Produkto',
    bulkCsvImport: 'Maramihang Pag-import (CSV)',
    searchProductsPlaceholder: 'Maghanap ng produkto...',
    allCategories: 'Lahat ng Kategorya',
    colArabicName: 'Pangalan sa Arabic',
    colEnglishName: 'Pangalan sa English',
    colPrice: 'Presyo (\uE900)',
    colCategory: 'Kategorya',
    colSku: 'SKU / Barcode',
    colActions: 'Aksyon',
    noProductsFound: 'Walang nahanap na produkto',
    quickAddPrompt: 'Magdagdag ng unang produkto ngayon',

    // Bill / Checkout
    orderBill: 'Resibo ng Kostumer',
    itemsSelected: 'napiling aytem',
    emptyCartTitle: 'Walang laman ang kariton',
    emptyCartDesc: 'Pindutin ang anumang produkto o maglagay ng presyo para maidagdag',
    clearCart: 'Alisin Lahat',
    unitPrice: 'bawat isa',
    subtotal: 'Kabuuang Halaga (Bago ang VAT):',
    vatAmount: 'Halaga ng VAT (15%):',
    grandTotal: 'Kabuuang Bayarin (Kasama ang VAT):',
    printReceiptBtn: 'I-print ang Resibo Agad (80mm)',
    emptyCartWarning: 'Walang laman ang kariton, magdagdag muna bago mag-print',
    itemAddedToast: 'Naidagdag',

    // Modals
    editProduct: 'I-edit ang Produkto',
    saveChanges: 'I-save ang Pagbabago',
    cancel: 'Kanselahin',
    deleteConfirm: 'Sigurado ka bang nais mong tanggalin ang produktong ito?',
    bulkModalTitle: 'Maramihang Pag-import ng Produkto (CSV / Excel)',
    bulkModalDesc: 'I-paste ang mga linya ng CSV mula sa Excel para magdagdag nang maramihan',
    downloadSampleCsv: 'I-download ang Halimbawang CSV',
    importNowBtn: 'I-import Lahat Ngayon',
    uploadingItems: 'Ina-upload ang mga aytem...',
    requiredColumnsOrder: 'Pagkakasunod ng Kolum: 1. Arabic Name • 2. English Name • 3. Presyo (\uE900) • 4. Kategorya • 5. Barcode',

    // VAT & Expense Hub Tab & Components
    tabVatHub: '📊 VAT at Expense Hub',
    vatHubTitle: 'VAT at Expense Hub (مركز الضريبة والمصروفات)',
    vatHubSubtitle: 'Balansehin ang POS sales VAT sa mga invoice ng pagbili at bumuo ng opisyal na quarterly ZATCA Form 100',
    quarterQ1: 'Quarter 1 (Q1)',
    quarterQ2: 'Quarter 2 (Q2)',
    quarterQ3: 'Quarter 3 (Q3)',
    quarterQ4: 'Quarter 4 (Q4)',
    recordExpenseBtn: 'Magtala ng Gastos / Vendor Bill',
    zatcaForm100Btn: 'ZATCA Form 100 Return',
    calculatingVatBalance: 'Kinakalkula ang balanse ng VAT...',
    outputVatTitle: 'Nasingil na Sales VAT (Output VAT)',
    box14Badge: 'Kahon 14',
    inputVatTitle: 'Deductible Purchases VAT (Input VAT)',
    box15Badge: 'Kahon 15',
    netVatDueTitle: 'Net VAT na Dapat Bayaran sa ZATCA',
    netVatRefundTitle: 'Net VAT Credit (Maaaring I-refund mula sa ZATCA)',
    box16Badge: 'Kahon 16',
    zatcaPolicyTitle: 'Patakaran sa Pagsunod sa Pagbawas ng ZATCA VAT:',
    zatcaPolicyDeductible: '✅ 15% Input VAT ay deductible mula sa: Imbentaryo, gasolina ng sasakyan, kuryente at tubig, internet at telecom, upa ng tindahan, at packaging.',
    zatcaPolicyNonDeductible: '❌ HINDI deductible: Sahod ng empleyado, bayarin/multa sa gobyerno, at personal na libangan.',
    expenseLogTitle: 'Talaan ng mga Gastos at Resibo ng Pagbili (Input VAT)',
    expenseLogSub: 'Lahat ng resibo ay awtomatikong kinukumpres sa 1800px (<250KB) upang makatipid sa imbakan',
    addBillBtn: 'Magdagdag ng Bill',
    colExpenseDate: 'Petsa',
    colVendor: 'Vendor / Tindahan',
    colExpenseCategory: 'Kategorya',
    colPaymentMethod: 'Paraan ng Pagbabayad',
    colNetAmount: 'Halaga Bago ang VAT',
    colVat15: 'VAT (15%)',
    colTotalPaid: 'Kabuuang Binayaran',
    colReceipt: 'Resibo',
    noExpensesLogged: 'Walang naitalang gastos. I-click ang "Magtala ng Gastos" para magdagdag ng resibo ng gasolina, kuryente, o imbentaryo.',
    viewReceiptTooltip: 'Tingnan ang orihinal na resibo',
    noAttachment: 'Walang kalakip',
    previewReceiptModalTitle: 'Orihinal na Resibo (Buong Resolusyon 1800px)',
    sarCurrency: 'SAR',
  },
};
